import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { withRouter } from "@impero-com/common/components/routerUtils";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import withI18n, { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { FormFrame, ButtonGroup, FormBox } from "@impero-com/ui";
import { NavigateFunction } from "react-router-dom";

import type { UserProfile } from "api/generated/model";
import {
    ExtendedF64Number,
    NewRiskDirectory,
    Section,
    AvailableActions,
    UserOverview,
    RiskDirectoryView,
} from "api/generated/model";
import { getRisks, postRisks } from "api/generated/risks/risks";
import { AccessRights, AccessRightsValue } from "components/accessRights";
import { RadioInput } from "components/form/legacyFields/radioInput";
import { StringInput } from "components/form/legacyFields/stringInput";
import { TextAreaInput } from "components/form/legacyFields/textAreaInput";
import { RISK_DIRECTORY_TAB } from "org/risks/content/types";
import {
    Condition,
    CreateRiskDirectory,
    FieldType,
    NodeTypeWithMetadata,
    OptionValue,
} from "org/risks/types";
import { ACCESS_RIGHTS } from "utils/accessRights/constants";
import {
    RiskDirectoryAccessRightsInstance,
    RiskDirectoryAccessRights,
} from "utils/accessRights/models";
import {
    FormCreate,
    FormRootComponentProps,
    hasError,
    subForm,
    ObjectError,
} from "utils/form";
import { generate } from "utils/guid";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";
import { Flippable } from "../../../components/debugPanel";
import { RISK_MODULE } from "../url";

const DETAILS_STEP = "DETAILS";
const PROCESS_MASTERDATA_STEP = "PROCESS_MASTERDATA";
const RISK_MASTERDATA_STEP = "RISK_MASTERDATA";
const ASSESSMENT_MASTERDATA_STEP = "ASSESSMENT_MASTERDATA";

type Step =
    | typeof DETAILS_STEP
    | typeof PROCESS_MASTERDATA_STEP
    | typeof RISK_MASTERDATA_STEP
    | typeof ASSESSMENT_MASTERDATA_STEP;

interface AppBarProps {
    ajaxManager: AjaxManager;
    orgProfileCookie: UserProfile | null;
}

interface RiskDirectoryValues {
    details: Details;
    test: TestValues;
}

interface Details {
    title: string;
    description: string;
    accessRights: AccessRightsValue<RiskDirectoryAccessRights>;
}

const TEST_DEFAULT_DIRECTORY_VALUE = "defaultDirectory";
const TEST_TEST_DIRECTORY_VALUE = "testDirectory";

interface TestValues {
    testDirectoryType:
        | typeof TEST_DEFAULT_DIRECTORY_VALUE
        | typeof TEST_TEST_DIRECTORY_VALUE;
}

interface RiskDirectoryFormProps
    extends FormRootComponentProps<RiskDirectoryValues>, AppBarProps {
    availableActions: AvailableActions;
    isCreateTestRiskDirectoryEnabled: boolean;
    navigate: NavigateFunction;
}

type RiskDirectoryStatus =
    | { type: "DEFAULT" }
    | { type: "LOADING" }
    | { type: "ERROR" };

interface RiskDirectoryFormState {
    currentStepId: Step;
    status: RiskDirectoryStatus;
}

class RiskDirectoryForm extends React.PureComponent<
    RiskDirectoryFormProps,
    RiskDirectoryFormState
> {
    state: RiskDirectoryFormState = {
        currentStepId: DETAILS_STEP,
        status: { type: "DEFAULT" },
    };

    render = () => {
        const { currentStepId } = this.state;
        const currentStep = this.renderSections[currentStepId]();

        return this.withApplicationBar(<Form>{currentStep}</Form>);
    };

    renderSections = {
        [DETAILS_STEP]: () => {
            const { orgProfileCookie, form } = this.props;
            const detailForm = subForm("details", form);

            const { getFieldsError, validateFields } = detailForm;

            let currentUser: UserOverview | undefined;
            if (orgProfileCookie) {
                const { userId, firstName, lastName, email } = orgProfileCookie;
                currentUser = {
                    id: userId,
                    firstname: firstName,
                    lastname: lastName,
                    email,
                };
            }
            const resourceAccessRights = RiskDirectoryAccessRightsInstance;

            return (
                <>
                    <FormFrame>
                        <FormBox>
                            <StringInput
                                type="CREATE"
                                id="title"
                                label={t("RiskDirectory_Label_Title")}
                                initialValue=""
                                validators={[
                                    {
                                        kind: "ruleset",
                                        required: true,
                                        whitespace: true,
                                    },
                                    {
                                        kind: "async",
                                        asyncValidator: checkTitleAvailability,
                                    },
                                ]}
                                form={detailForm}
                            />
                        </FormBox>
                        <FormBox>
                            <TextAreaInput
                                type="CREATE"
                                id="description"
                                label={t("RiskDirectory_Label_Description")}
                                initialValue=""
                                form={detailForm}
                            />
                        </FormBox>
                        <FormBox>
                            <AccessRights
                                type="CREATE"
                                id="accessRights"
                                initialValue={{
                                    responsible: currentUser,
                                    usersAndRights: currentUser
                                        ? [
                                              {
                                                  user: currentUser,
                                                  accessRight:
                                                      ACCESS_RIGHTS.EDIT,
                                              },
                                          ]
                                        : [],
                                }}
                                form={detailForm}
                                resourceAccessRights={resourceAccessRights}
                            />
                        </FormBox>
                        <ButtonGroup
                            buttonSpecs={[
                                {
                                    id: "risk-directory-details-next",
                                    label: t("RiskDirectory_Next"),
                                    type: "primary",
                                    disabled: hasError(
                                        getFieldsError() as ObjectError
                                    ),
                                    onClick: () =>
                                        validateFields((errors) => {
                                            if (!errors) {
                                                this.setState({
                                                    currentStepId:
                                                        PROCESS_MASTERDATA_STEP,
                                                });
                                            }
                                        }),
                                },
                            ]}
                        />
                    </FormFrame>
                </>
            );
        },
        [PROCESS_MASTERDATA_STEP]: () => (
            <>
                <FormFrame>
                    <h1>{PROCESS_MASTERDATA_STEP}</h1>
                    <ButtonGroup
                        buttonSpecs={[
                            {
                                id: "risk-directory-process-masterdata-next",
                                label: t("RiskDirectory_Next"),
                                type: "primary",
                                onClick: () =>
                                    this.setState({
                                        currentStepId: RISK_MASTERDATA_STEP,
                                    }),
                            },
                            {
                                id: "risk-directory-process-masterdata-prev",
                                label: t("RiskDirectory_Previous"),
                                type: "text",
                                onClick: () =>
                                    this.setState({
                                        currentStepId: DETAILS_STEP,
                                    }),
                            },
                        ]}
                    />
                </FormFrame>
            </>
        ),
        [RISK_MASTERDATA_STEP]: () => (
            <>
                <FormFrame>
                    <h1>{RISK_MASTERDATA_STEP}</h1>
                    <ButtonGroup
                        buttonSpecs={[
                            {
                                id: "risk-directory-risk-masterdata-next",
                                label: t("RiskDirectory_Next"),
                                type: "primary",
                                onClick: () =>
                                    this.setState({
                                        currentStepId:
                                            ASSESSMENT_MASTERDATA_STEP,
                                    }),
                            },
                            {
                                id: "risk-directory-risk-masterdata-prev",
                                label: t("RiskDirectory_Previous"),
                                type: "text",
                                onClick: () =>
                                    this.setState({
                                        currentStepId: PROCESS_MASTERDATA_STEP,
                                    }),
                            },
                        ]}
                    />
                </FormFrame>
            </>
        ),
        [ASSESSMENT_MASTERDATA_STEP]: () => (
            <>
                <FormFrame>
                    <h1>{ASSESSMENT_MASTERDATA_STEP}</h1>
                    <ButtonGroup
                        buttonSpecs={[
                            {
                                id: "risk-directory-assessment-masterdata-save",
                                label: t(
                                    "RiskDirectory_Creator_CreateDirectory"
                                ),
                                type: "primary",
                                onClick: () => this.save(),
                            },
                            {
                                id: "risk-directory-assessment-masterdata-prev",
                                label: t("RiskDirectory_Previous"),
                                type: "text",
                                onClick: () =>
                                    this.setState({
                                        currentStepId: RISK_MASTERDATA_STEP,
                                    }),
                            },
                        ]}
                    />
                </FormFrame>
            </>
        ),
    };

    withApplicationBar = (children: React.ReactNode) => {
        const { currentStepId } = this.state;

        return (
            <OrgApplicationBar
                title={{
                    title: t("RiskDirectory_Creator_Title"),
                    breadCrumbs: [
                        {
                            label: t("RiskDirectory_RiskManager"),
                            url: RISK_MODULE,
                        },
                    ],
                }}
                navigation={{
                    type: "Steps",
                    currentStepId,
                    stepSpecs: [
                        {
                            id: DETAILS_STEP,
                            label: t("RiskDirectory_Creator_Step_Details"),
                            onClick: () =>
                                this.setState({ currentStepId: DETAILS_STEP }),
                        },
                        {
                            id: PROCESS_MASTERDATA_STEP,
                            label: t(
                                "RiskDirectory_Creator_Step_Process_Masterdata"
                            ),
                            onClick: () => {},
                            disabled: { type: "Disabled" },
                        },
                        {
                            id: RISK_MASTERDATA_STEP,
                            label: t(
                                "RiskDirectory_Creator_Step_Risk_Masterdata"
                            ),
                            onClick: () => {},
                        },
                        {
                            id: ASSESSMENT_MASTERDATA_STEP,
                            label: t(
                                "RiskDirectory_Creator_Step_Assessment_Masterdata"
                            ),
                            onClick: () => {},
                            disabled: { type: "Disabled" },
                        },
                    ],
                }}
            >
                {children}
            </OrgApplicationBar>
        );
    };

    save = () => {
        const {
            form: { validateFields },
            ajaxManager,
            navigate,
        } = this.props;

        validateFields({ force: true }, (errors, values) => {
            if (errors) {
                return;
            }
            const { title, description, accessRights } = values.details;

            const data: CreateRiskDirectory = {
                riskDirectory: newRiskDirectory(title, description),
                responsibleId: accessRights.responsible!.id,
                userIdAndRights: accessRights.usersAndRights.map(
                    ({ user, accessRight }) => ({
                        userId: user.id,
                        right: accessRight,
                    })
                ),
            };

            ajaxManager
                .ajax<RiskDirectoryView, RiskDirectoryStatus>(
                    () => postRisks(data),
                    {
                        component: this,
                        initialStatusValue: "DEFAULT",
                        getErrorStatus: () => ({ type: "DEFAULT" }),
                        getSuccessStatus: () => ({ type: "DEFAULT" }),
                        inFlightStatus: { type: "LOADING" },
                    }
                )
                .then((riskDirectory) =>
                    navigate(`/risks/${riskDirectory.id}#${RISK_DIRECTORY_TAB}`)
                );
        });
    };
}

function checkTitleAvailability(title: string | undefined): Promise<void> {
    if (title === undefined) {
        return Promise.resolve(); // Should be ruled-out by required: true
    }

    return getRisks({ all_directories: true, title }).then((response) => {
        const matchedTitles = response.data;
        if (matchedTitles.content.length > 0) {
            return Promise.reject(
                new Error(t("RiskDirectory_Creator_Error_TitleExists"))
            );
        } else {
            return Promise.resolve();
        }
    });
}

function newOptionsWithDefaultWeights(
    options: Array<string | Omit<OptionValue, "weight" | "rangeMin">>
): Array<OptionValue> {
    return options.map((option, index) => {
        const weight = index;
        const rangeMin: ExtendedF64Number =
            index === 0
                ? { type: "NegInfinity" }
                : { type: "Number", value: index };
        if (typeof option === "string") {
            return {
                id: generate(),
                value: option,
                weight,
                rangeMin,
            };
        } else {
            return {
                id: option.id,
                value: option.value,
                weight,
                rangeMin,
            };
        }
    });
}

function newRiskDirectory(
    title: string,
    description: string
): NewRiskDirectory {
    const inherentImpactId = generate();
    const inherentLikelihoodId = generate();
    const responseHeadingId = generate();
    const responseId = generate();
    const residualImpactId = generate();
    const residualLikelihoodId = generate();
    const financialStatementAssertionsId = generate();
    const objectivesId = generate();
    const commentId = generate();

    const responseReduceId = generate();

    return {
        title,
        description,
        structure: {
            blessedFieldIds: {
                inherentImpactId,
                inherentLikelihoodId,
                residualImpactId,
                residualLikelihoodId,
                responseId,
            },
            nodeTypeToDirectoryLevelFields: {
                Risk: [
                    {
                        id: financialStatementAssertionsId,
                        name: t(
                            "RiskMaps_Edit_Metadadata_FinancialStatementAssertions"
                        ),
                        fieldType: {
                            type: "CheckboxGroup",
                            options: newOptionsWithDefaultWeights([
                                t(
                                    "RiskMaps_Edit_Metadadata_FinancialStatementAssertions_Completeness"
                                ),
                                t(
                                    "RiskMaps_Edit_Metadadata_FinancialStatementAssertions_ValuationOrAllocation"
                                ),
                                t(
                                    "RiskMaps_Edit_Metadadata_FinancialStatementAssertions_ExistenceOrOccurrence"
                                ),
                                t(
                                    "RiskMaps_Edit_Metadadata_FinancialStatementAssertions_PresentationAndDisclosure"
                                ),
                                t(
                                    "RiskMaps_Edit_Metadadata_FinancialStatementAssertions_RightsAndObligations"
                                ),
                            ]),
                        },
                        condition: null,
                        requiredForAssessment: false,
                    },
                    {
                        id: objectivesId,
                        name: t("RiskMaps_Edit_Metadadata_Objectives"),
                        fieldType: {
                            type: "CheckboxGroup",
                            options: newOptionsWithDefaultWeights([
                                t(
                                    "RiskMaps_Edit_Metadadata_Objectives_Strategic"
                                ),
                                t(
                                    "RiskMaps_Edit_Metadadata_Objectives_Operations"
                                ),
                                t(
                                    "RiskMaps_Edit_Metadadata_Objectives_Reporting"
                                ),
                                t(
                                    "RiskMaps_Edit_Metadadata_Objectives_Compliance"
                                ),
                            ]),
                        },
                        condition: null,
                        requiredForAssessment: false,
                    },
                ],
            },
            entityLevelFields: [
                {
                    id: inherentImpactId,
                    name: t("RiskMaps_Edit_Metadata_InherentImpact"),
                    fieldType: {
                        type: "Dropdown",
                        options: newOptionsWithDefaultWeights([
                            t("RiskMaps_Edit_Metadata_Impact_VeryLow"),
                            t("RiskMaps_Edit_Metadata_Impact_Low"),
                            t("RiskMaps_Edit_Metadata_Impact_Medium"),
                            t("RiskMaps_Edit_Metadata_Impact_High"),
                            t("RiskMaps_Edit_Metadata_Impact_VeryHigh"),
                        ]),
                    },
                    condition: null,
                    requiredForAssessment: false,
                },
                {
                    id: inherentLikelihoodId,
                    name: t("RiskMaps_Edit_Metadata_InherentLikelihood"),
                    fieldType: {
                        type: "Dropdown",
                        options: newOptionsWithDefaultWeights([
                            t("RiskMaps_Edit_Metadata_Likelihood_Remote"),
                            t("RiskMaps_Edit_Metadata_Likelihood_Unlikely"),
                            t("RiskMaps_Edit_Metadata_Likelihood_Likely"),
                            t("RiskMaps_Edit_Metadata_Likelihood_HighlyLikely"),
                            t(
                                "RiskMaps_Edit_Metadata_Likelihood_NearlyCertain"
                            ),
                        ]),
                    },
                    condition: null,
                    requiredForAssessment: false,
                },
                {
                    id: responseHeadingId,
                    name: t("RiskMapResponse_Columns_Response"),
                    fieldType: { type: "Heading" },
                    condition: null,
                    requiredForAssessment: false,
                },
                {
                    id: responseId,
                    name: t("RiskMapResponse_Columns_Response"),
                    fieldType: {
                        type: "Dropdown",
                        options: newOptionsWithDefaultWeights([
                            t("RiskMapResponse_NotApplicable"),
                            t("RiskMapResponse_Responses_Transfer"),
                            t("RiskMapResponse_Responses_Avoid"),
                            {
                                id: responseReduceId,
                                value: t("RiskMapResponse_Responses_Reduce"),
                            },
                            t("RiskMapResponse_Responses_Accept"),
                        ]),
                    },
                    condition: null,
                    requiredForAssessment: false,
                },
                {
                    id: residualImpactId,
                    name: t("RiskMaps_Edit_Metadata_ResidualImpact"),
                    fieldType: {
                        type: "Dropdown",
                        options: newOptionsWithDefaultWeights([
                            t("RiskMaps_Edit_Metadata_Impact_VeryLow"),
                            t("RiskMaps_Edit_Metadata_Impact_Low"),
                            t("RiskMaps_Edit_Metadata_Impact_Medium"),
                            t("RiskMaps_Edit_Metadata_Impact_High"),
                            t("RiskMaps_Edit_Metadata_Impact_VeryHigh"),
                        ]),
                    },
                    condition: {
                        action: "Show",
                        operator: "Equal",
                        columnId: responseId,
                        value: responseReduceId,
                    },
                    requiredForAssessment: false,
                },
                {
                    id: residualLikelihoodId,
                    name: t("RiskMaps_Edit_Metadata_ResidualLikelihood"),
                    fieldType: {
                        type: "Dropdown",
                        options: newOptionsWithDefaultWeights([
                            t("RiskMaps_Edit_Metadata_Likelihood_Remote"),
                            t("RiskMaps_Edit_Metadata_Likelihood_Unlikely"),
                            t("RiskMaps_Edit_Metadata_Likelihood_Likely"),
                            t("RiskMaps_Edit_Metadata_Likelihood_HighlyLikely"),
                            t(
                                "RiskMaps_Edit_Metadata_Likelihood_NearlyCertain"
                            ),
                        ]),
                    },
                    condition: {
                        action: "Show",
                        operator: "Equal",
                        columnId: responseId,
                        value: responseReduceId,
                    },
                    requiredForAssessment: false,
                },
                {
                    id: commentId,
                    name: t("RiskMaps_Edit_Metadata_Comment"),
                    fieldType: { type: "TextArea" },
                    condition: null,
                    requiredForAssessment: false,
                },
            ],
            sections: [
                {
                    id: generate(),
                    sectionColumns: [
                        [financialStatementAssertionsId, objectivesId],
                    ],
                },
                {
                    id: generate(),
                    sectionColumns: [
                        [inherentImpactId],
                        [inherentLikelihoodId],
                    ],
                },
                {
                    id: generate(),
                    sectionColumns: [[responseHeadingId, responseId]],
                },
                {
                    id: generate(),
                    sectionColumns: [
                        [residualImpactId],
                        [residualLikelihoodId],
                    ],
                },
                {
                    id: generate(),
                    sectionColumns: [[commentId]],
                },
            ],
        },
    };
}

function newTestRiskDirectory(
    title: string,
    description: string
): NewRiskDirectory {
    const directory = newRiskDirectory(title, description);
    const { structure } = directory;
    // Remove unnecessary stuff
    {
        const section = structure.sections.splice(0, 1)[0];
        structure.nodeTypeToDirectoryLevelFields["Risk"] =
            structure.nodeTypeToDirectoryLevelFields["Risk"].filter(
                (field) =>
                    !section.sectionColumns.some((col) =>
                        col.some((f) => f === field.id)
                    )
            );
    }

    let insertSectionAt = 0;

    const addDirectorySection = (
        nodeType: NodeTypeWithMetadata,
        columnCount: number
    ) => {
        const sectionColumns = new Array(columnCount);
        for (let i = 0; i < sectionColumns.length; ++i) {
            sectionColumns[i] = [];
        }
        const section: Section = {
            id: generate(),
            sectionColumns,
        };
        structure.sections.splice(insertSectionAt++, 0, section);

        const addToSection = (
            column: number,
            name: string,
            fieldType: FieldType,
            condition?: Condition | null
        ) => {
            if (!structure.nodeTypeToDirectoryLevelFields[nodeType]) {
                structure.nodeTypeToDirectoryLevelFields[nodeType] = [];
            }
            const id = generate();
            structure.nodeTypeToDirectoryLevelFields[nodeType].push({
                id,
                name,
                fieldType,
                condition: condition ?? null,
                requiredForAssessment: false,
            });
            section.sectionColumns[column % section.sectionColumns.length].push(
                id
            );
            return id;
        };

        return addToSection;
    };

    const addEntitySection = (columnCount: number) => {
        const sectionColumns = new Array(columnCount);
        for (let i = 0; i < sectionColumns.length; ++i) {
            sectionColumns[i] = [];
        }
        const section: Section = {
            id: generate(),
            sectionColumns,
        };
        structure.sections.splice(insertSectionAt++, 0, section);

        const addToSection = (
            column: number,
            name: string,
            fieldType: FieldType,
            condition?: Condition | null
        ) => {
            const id = generate();
            structure.entityLevelFields.push({
                id,
                name,
                fieldType,
                condition: condition ?? null,
                requiredForAssessment: false,
            });
            section.sectionColumns[column % section.sectionColumns.length].push(
                id
            );
            return id;
        };

        return addToSection;
    };

    // Add directory level fields for risks and processes
    for (const nodeType of ["Process" as const, "Risk" as const]) {
        let addToSection = addDirectorySection(nodeType, 1);

        const showId = generate();
        const hideId = generate();
        addToSection(0, `Let's go ${nodeType.toLowerCase()} s!`, {
            type: "Heading",
        });
        const switcherId = addToSection(0, "Headings", {
            type: "RadioButtonGroup",
            options: newOptionsWithDefaultWeights([
                { id: showId, value: "Show" },
                { id: hideId, value: "Hide" },
            ]),
        });

        addToSection = addDirectorySection(
            nodeType,
            nodeType === "Process" ? 2 : 1
        );

        addToSection(
            0,
            `Simple ${nodeType.toLowerCase()} fields`,
            { type: "Heading" },
            {
                action: "Show",
                columnId: switcherId,
                operator: "Equal",
                value: showId,
            }
        );
        addToSection(1, `A text input for ${nodeType.toLowerCase()} s`, {
            type: "TextInput",
        });
        addToSection(0, `A text area for ${nodeType.toLowerCase()} s`, {
            type: "TextArea",
        });
        addToSection(1, `A numeric input for ${nodeType.toLowerCase()} s`, {
            type: "NumericInput",
        });

        addToSection = addDirectorySection(
            nodeType,
            nodeType === "Process" ? 2 : 1
        );

        addToSection(
            0,
            `${nodeType} fields with options`,
            { type: "Heading" },
            {
                action: "Show",
                columnId: switcherId,
                operator: "Equal",
                value: showId,
            }
        );
        addToSection(0, `A checkbox group for ${nodeType.toLowerCase()} s`, {
            type: "CheckboxGroup",
            options: newOptionsWithDefaultWeights([
                { id: generate(), value: "Check A" },
                { id: generate(), value: "Check B" },
                { id: generate(), value: "Check C" },
            ]),
        });
        addToSection(1, `A radio group for ${nodeType.toLowerCase()} s`, {
            type: "RadioButtonGroup",
            options: newOptionsWithDefaultWeights([
                { id: generate(), value: "Radio ga ga" },
                { id: generate(), value: "Radio goo goo" },
            ]),
        });
        addToSection(1, `A dropdown for ${nodeType.toLowerCase()} s`, {
            type: "Dropdown",
            options: newOptionsWithDefaultWeights([
                { id: generate(), value: "Oops" },
                { id: generate(), value: "I did it again" },
            ]),
        });

        addToSection = addDirectorySection(
            nodeType,
            nodeType === "Process" ? 2 : 1
        );

        addToSection(
            0,
            `Advanced ${nodeType.toLowerCase()} fields`,
            { type: "Heading" },
            {
                action: "Show",
                columnId: switcherId,
                operator: "Equal",
                value: showId,
            }
        );
        addToSection(0, `Attached files for ${nodeType.toLowerCase()} s`, {
            type: "AttachedFiles",
        });
        addToSection(
            1,
            `A heading`,
            { type: "Heading" },
            {
                action: "Show",
                columnId: switcherId,
                operator: "Equal",
                value: showId,
            }
        );
    }

    // Add entity level fields
    {
        let addToSection = addEntitySection(1);

        const showId = generate();
        const hideId = generate();
        addToSection(0, `Let's go entities!`, { type: "Heading" });
        const switcherId = addToSection(0, "Headings", {
            type: "RadioButtonGroup",
            options: newOptionsWithDefaultWeights([
                { id: showId, value: "Show" },
                { id: hideId, value: "Hide" },
            ]),
        });

        addToSection = addEntitySection(1);

        addToSection(
            0,
            `Simple entity fields`,
            { type: "Heading" },
            {
                action: "Show",
                columnId: switcherId,
                operator: "Equal",
                value: showId,
            }
        );
        addToSection(1, `A text input for entities`, { type: "TextInput" });
        addToSection(0, `A text area for entities`, { type: "TextArea" });
        addToSection(1, `A numeric input for entities`, {
            type: "NumericInput",
        });

        addToSection = addEntitySection(1);

        addToSection(
            0,
            `Entity fields with options`,
            { type: "Heading" },
            {
                action: "Show",
                columnId: switcherId,
                operator: "Equal",
                value: showId,
            }
        );
        addToSection(0, `A checkbox group for entities`, {
            type: "CheckboxGroup",
            options: newOptionsWithDefaultWeights([
                { id: generate(), value: "Check A" },
                { id: generate(), value: "Check B" },
                { id: generate(), value: "Check C" },
            ]),
        });
        addToSection(1, `A radio group for entities`, {
            type: "RadioButtonGroup",
            options: newOptionsWithDefaultWeights([
                { id: generate(), value: "Radio ga ga" },
                { id: generate(), value: "Radio goo goo" },
            ]),
        });
        addToSection(1, `A dropdown for entities`, {
            type: "Dropdown",
            options: newOptionsWithDefaultWeights([
                { id: generate(), value: "Oops" },
                { id: generate(), value: "I did it again" },
            ]),
        });

        addToSection = addEntitySection(1);

        addToSection(
            0,
            `Advanced entity fields`,
            { type: "Heading" },
            {
                action: "Show",
                columnId: switcherId,
                operator: "Equal",
                value: showId,
            }
        );
        addToSection(0, `Attached files for entities`, {
            type: "AttachedFiles",
        });
        addToSection(
            1,
            `A heading`,
            { type: "Heading" },
            {
                action: "Show",
                columnId: switcherId,
                operator: "Equal",
                value: showId,
            }
        );
    }

    return directory;
}

class SimpleRiskDirectoryForm extends React.PureComponent<
    RiskDirectoryFormProps,
    RiskDirectoryFormState
> {
    state: RiskDirectoryFormState = {
        currentStepId: DETAILS_STEP,
        status: { type: "DEFAULT" },
    };

    userHasAccess = () => this.props.availableActions.risk.riskDirectory.create;

    render = () => {
        return this.withApplicationBar(
            this.userHasAccess() ? (
                <Form>{this.renderForm()}</Form>
            ) : (
                <NoAccessCard />
            )
        );
    };

    renderForm = () => {
        const { orgProfileCookie, form } = this.props;
        const detailForm = subForm("details", form);
        const testForm = subForm("test", form);

        const { getFieldsError } = detailForm;

        let currentUser: UserOverview | undefined;
        if (orgProfileCookie) {
            const { userId, firstName, lastName, email } = orgProfileCookie;
            currentUser = {
                id: userId,
                firstname: firstName,
                lastname: lastName,
                email,
            };
        }
        const resourceAccessRights = RiskDirectoryAccessRightsInstance;

        return (
            <>
                <FormFrame>
                    <FormBox>
                        <StringInput
                            type="CREATE"
                            id="title"
                            label={t("RiskDirectory_Label_Title")}
                            initialValue=""
                            validators={[
                                {
                                    kind: "ruleset",
                                    required: true,
                                    whitespace: true,
                                },
                                {
                                    kind: "async",
                                    asyncValidator: checkTitleAvailability,
                                },
                            ]}
                            form={detailForm}
                        />
                    </FormBox>
                    <FormBox>
                        <TextAreaInput
                            type="CREATE"
                            id="description"
                            label={t("RiskDirectory_Label_Description")}
                            initialValue=""
                            form={detailForm}
                        />
                    </FormBox>
                    <FormBox>
                        <AccessRights
                            type="CREATE"
                            id="accessRights"
                            initialValue={{
                                responsible: currentUser,
                                usersAndRights: currentUser
                                    ? [
                                          {
                                              user: currentUser,
                                              accessRight: ACCESS_RIGHTS.ADMIN,
                                          },
                                      ]
                                    : [],
                            }}
                            form={detailForm}
                            resourceAccessRights={resourceAccessRights}
                        />
                    </FormBox>
                    <Flippable feature="createTestRiskDirectory">
                        <FormBox>
                            <RadioInput
                                type="CREATE"
                                id="testDirectoryType"
                                label="[Test-Only] Directory type"
                                options={[
                                    {
                                        label: "Default",
                                        value: TEST_DEFAULT_DIRECTORY_VALUE,
                                    },
                                    {
                                        label: "Test",
                                        value: TEST_TEST_DIRECTORY_VALUE,
                                    },
                                ]}
                                columns={2}
                                initialValue={TEST_DEFAULT_DIRECTORY_VALUE}
                                form={testForm}
                            />
                        </FormBox>
                    </Flippable>
                    <ButtonGroup
                        buttonSpecs={[
                            {
                                id: "risk-directory-create",
                                label: t(
                                    "RiskDirectory_Creator_CreateDirectory"
                                ),
                                type: "primary",
                                disabled: hasError(
                                    getFieldsError() as ObjectError
                                ),
                                onClick: () => this.save(),
                            },
                            {
                                id: "risk-directory-creator-cancel",
                                label: t("Cancel"),
                                type: "default",
                                onClick: () => this.props.navigate(RISK_MODULE),
                            },
                        ]}
                    />
                </FormFrame>
            </>
        );
    };

    withApplicationBar = (children: React.ReactNode) => {
        return (
            <OrgApplicationBar
                title={{
                    title: t("RiskDirectory_Creator_Title"),
                    breadCrumbs: [
                        {
                            label: t("RiskDirectory_RiskManager"),
                            url: RISK_MODULE,
                        },
                    ],
                }}
            >
                {children}
            </OrgApplicationBar>
        );
    };

    save = () => {
        const {
            form: { validateFields },
            ajaxManager,
            navigate,
        } = this.props;

        validateFields({ force: true }, (errors, values): void => {
            if (errors) {
                return;
            }
            const { title, description, accessRights } = values.details;

            let riskDirectory: NewRiskDirectory;
            if (!this.props.isCreateTestRiskDirectoryEnabled) {
                riskDirectory = newRiskDirectory(title, description);
            } else {
                switch (values.test.testDirectoryType) {
                    case TEST_DEFAULT_DIRECTORY_VALUE: {
                        riskDirectory = newRiskDirectory(title, description);
                        break;
                    }
                    case TEST_TEST_DIRECTORY_VALUE: {
                        riskDirectory = newTestRiskDirectory(
                            title,
                            description
                        );
                        break;
                    }
                    default: {
                        return assertNever(values.test.testDirectoryType);
                    }
                }
            }
            const data: CreateRiskDirectory = {
                riskDirectory,
                responsibleId: accessRights.responsible!.id,
                userIdAndRights: accessRights.usersAndRights.map(
                    ({ user, accessRight }) => ({
                        userId: user.id,
                        right: accessRight,
                    })
                ),
            };

            ajaxManager
                .ajax<RiskDirectoryView, RiskDirectoryStatus>(
                    () => postRisks(data),
                    {
                        component: this,
                        initialStatusValue: "DEFAULT",
                        getErrorStatus: () => ({ type: "DEFAULT" }),
                        getSuccessStatus: () => ({ type: "DEFAULT" }),
                        inFlightStatus: { type: "LOADING" },
                    }
                )
                .then((riskDirectory) =>
                    navigate(`/risks/${riskDirectory.id}#${RISK_DIRECTORY_TAB}`)
                );
        });
    };
}

const _riskDirectoryCreator = FormCreate<
    RiskDirectoryValues,
    RiskDirectoryFormProps
>()(withRouter(withI18n<RiskDirectoryFormProps>(RiskDirectoryForm)));

export const SimpleRiskDirectoryCreator = withRouter(
    FormCreate<RiskDirectoryValues, RiskDirectoryFormProps>()(
        withI18n<RiskDirectoryFormProps>(SimpleRiskDirectoryForm)
    )
);
